import { useTranslation } from 'react-i18next'
import residenceTranslationInfo from './residenceTranslationInfo'
import useTimelineEventDescription, { DEATH_OF_PARENT_1, DEATH_OF_PARENT_2 } from './useTimelineEventDescription'
import GedcomX from '../../../../../shared/helpers/gedcom_X'
import { compareYears, getYearsDiffDates } from '../../../../../shared/helpers/factDate_Helper'
import mapFactToLifeEvent from './mapFactToLifeEvent'

const gcx = GedcomX()

export default function useLifeEvents({ personData, parent1, parent2 }) {
  const [t] = useTranslation()

  const parent1Info = getParentInfo(parent1)
  const parent2Info = getParentInfo(parent2)
  const personInfo = getPersonInfo(personData, parent1Info, parent2Info)

  // Translations are defined here to avoid passing t to child functions
  const translations = {
    [gcx.BIRTH]: {
      name: t('wcf.person-timeline.event-birth', 'Birth'),
      description: useTimelineEventDescription(personInfo, gcx.BIRTH, 0),
    },
    [gcx.DEATH]: {
      name: t('wcf.person-timeline.event-death', 'Death'),
      description: useTimelineEventDescription(personInfo, gcx.DEATH, 0),
    },
    [gcx.BURIAL]: {
      name: t('wcf.person-timeline.event-burial', 'Burial'),
      description: useTimelineEventDescription(personInfo, gcx.BURIAL, 0),
    },
    [gcx.RESIDENCE]: {
      name: t('wcf.person-timeline.event-residence', 'Residence'),
      description: (fact, year, ageAtEvent) =>
        t(...residenceTranslationInfo(personData, year, fact.display.place, ageAtEvent, t)),
    },
  }

  const parentDeathEventNameGenderMap = {
    [gcx.MALE]: t('wcf.person-timeline.event-death-of-father', 'Death of father'),
    [gcx.FEMALE]: t('wcf.person-timeline.event-death-of-mother', 'Death of mother'),
    default: t('wcf.person-timeline.event-death-of-parent', 'Death of parent'),
  }

  const personBirthFact = getPersonFact(personData, gcx.BIRTH)
  const birthDate =
    personBirthFact && personBirthFact.dateDescription ? new Date(personBirthFact.dateDescription) : null

  let lifeEvents = []
  // eslint-disable-next-line no-warning-comments
  // TODO: Determine what it would take to allow an early exit if this is the case
  // because otherwise we're just left with information about parent deaths, if they exist.
  // What's preventing this is React's requirement that all hooks defined in the function body
  // be called the same way every time
  if (personData && personData.facts) {
    lifeEvents = personData.facts.map(buildFactToEventMapper(translations, birthDate)).filter((fact) => !!fact)
  }

  const birthYear = personData.lifespan.split('-')[0]
  const deathOfParent1Text = useTimelineEventDescription(personInfo, DEATH_OF_PARENT_1, personInfo.parent1.deathAge)
  const deathOfParent2Text = useTimelineEventDescription(personInfo, DEATH_OF_PARENT_2, personInfo.parent2.deathAge)

  pushParentDeathEvent(
    parent1Info,
    'parent 1',
    deathOfParent1Text,
    birthYear,
    parentDeathEventNameGenderMap,
    lifeEvents
  )
  pushParentDeathEvent(
    parent2Info,
    'parent 2',
    deathOfParent2Text,
    birthYear,
    parentDeathEventNameGenderMap,
    lifeEvents
  )

  lifeEvents.sort((a, b) => compareYears(a.year, b.year))

  return { lifeEvents, deathYear: personInfo.deathYear }
}

function buildFactToEventMapper(translations, birthDate) {
  return (fact) => mapFactToLifeEvent({ fact, translations, birthDate })
}

function getParentDeathMessageForGender(gender, genderMap) {
  switch (gender) {
    case 'male':
      return genderMap[gcx.MALE]
    case 'female':
      return genderMap[gcx.FEMALE]
    default:
      return genderMap.default
  }
}

function pushParentDeathEvent(parentInfo, eventType, deathOfParentText, childBirthYear, genderMap, events) {
  if (!parentInfo?.deathYear) {
    return
  }

  const event = getParentDeathMessageForGender(parentInfo.gender, genderMap)

  events.push({
    year: parentInfo.deathYear,
    exactDate: parentInfo.deathDate,
    place: `${parentInfo.deathLocation}`,
    event,
    eventType: `Death of ${eventType}`,
    description: deathOfParentText,
    age: parentInfo.deathYear - childBirthYear,
  })
}

function getPersonInfo(person, parent1Info, parent2Info) {
  if (!person) {
    return ''
  }

  let deathYear = ''

  const facts = getPersonFacts(person)
  const birth = facts[gcx.BIRTH]
  const christening = facts[gcx.CHRISTENING]
  const residence = facts[gcx.RESIDENCE]
  const death = facts[gcx.DEATH]
  const burial = facts[gcx.BURIAL]

  if (death?.display?.date?.year) {
    deathYear = facts[gcx.DEATH].display.date.year
  }

  return {
    name: person.name,
    surname: person.surname,
    gender: person.gender,
    living: person.living ?? '',
    parent1: parent1Info,
    parent2: parent2Info,
    birthDate: getEventDate(birth),
    birthLocation: getLocationFact(birth),
    christeningDate: getEventDate(christening),
    christeningLocation: getLocationFact(christening),
    residenceDate: getEventDate(residence),
    residenceLocation: getLocationFact(residence),
    residenceAge: '',
    deathDate: getEventDate(death),
    deathYear,
    deathLocation: getLocationFact(death),
    deathAge: getAgeLivingOrAtDeath(person.living, birth, death),
    burialDate: getEventDate(burial),
    burialLocation: getLocationFact(burial),
  }
}

function getParentInfo(parent) {
  if (!parent) {
    return ''
  }

  const facts = getPersonFacts(parent)
  const birth = facts[gcx.BIRTH]
  const death = facts[gcx.DEATH]
  const burial = facts[gcx.BURIAL]

  return {
    name: parent.name ?? '',
    surname: parent.surname ?? '',
    gender: parent.gender ?? '',
    living: parent.living ?? '',
    deathDate: getEventDate(death),
    deathYear: getParentDeathYear(parent),
    deathLocation: getLocationFact(death),
    deathAge: getAgeLivingOrAtDeath(parent.living, birth, death),
    burialDate: getEventDate(burial),
    burialLocation: getLocationFact(burial),
  }
}

function getPersonFacts(person) {
  // Uses Proxy's `get` (https://developer.mozilla.org/en-US/docs/Web/JavaScript/Reference/Global_Objects/Proxy)
  // to get facts as needed
  const factsCacheHandler = {
    get: (facts, factType) => {
      if (factType in facts) {
        // Cached response
        return facts[factType]
      }

      facts[factType] = getPersonFact(person, factType)
      // Will also just return '' if no fact is found, acting as a transparent passthrough for getPersonFact
      return facts[factType]
    },
  }
  return new Proxy({}, factsCacheHandler)
}

function getPersonFact(person, factType) {
  if (!person?.facts) {
    return null
  }

  return person.facts.find((fact) => fact.type === factType) ?? ''
}

function getEventDate(fact) {
  let date = ''

  if (fact && fact.dateDescription) {
    date = fact.dateDescription
  } else if (fact && fact.display) {
    date = fact.display.date.normalized
  }

  return date
}

function getLocationFact(fact) {
  if (!fact) {
    return ''
  }

  return fact.display ? fact.display.placeFull : fact.placeDescription
}

function getParentDeathYear(parent) {
  if (!parent?.lifespan) {
    return ''
  }

  return parseInt(parent.lifespan.split('-')[1], 10)
}

function getAgeLivingOrAtDeath(living, birthFact, deathFact) {
  if (!birthFact?.display?.date?.normalized) {
    return ''
  }

  let age = ''
  let fullDeathDate = ''
  let fullBirthDate = ''

  try {
    if (deathFact && deathFact.display && deathFact.display.date) {
      age = deathFact.display.date.year - birthFact.display.date.year
      fullDeathDate = new Date(deathFact.dateDescription)
      fullBirthDate = new Date(birthFact.dateDescription)
      age = getYearsDiffDates(fullBirthDate, fullDeathDate)
    } else if (living) {
      fullBirthDate = new Date(birthFact.dateDescription)
      age = getYearsDiffDates(fullBirthDate, new Date())
    }
  } catch (e) {
    age = ''
  }

  return age
}
